/* eslint-disable no-param-reassign */
import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import type { SrtStream } from '../../../stores/types/video';
import type { DropCallback } from '../../../stores/types/user-interface';
import { DRAG_TYPE } from '../../../stores/reducers/user-interface';
import useDropArea from '../../../hooks/use-dnd/use-drop-area';
import { GreenroomState as GS } from '../../../stores/types/types';
import Portal from '../../general-components/portal';
import { videoGridClasses } from '../../../types/constants';
import V_LAYOUTS from '../../../utils/video-layout';
import useShots from '../../../contexts/shots/provider';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import { ROOM_STATES } from '../../../stores/reducers/settings';
import { FEED_TYPES } from '../../../stores/reducers/shots';
import useEchoCancellation, { shouldDoEchoCancel } from '../../../hooks/use-echo-cancellation';
import useAudioControls from './use-audio-controls';
import { UpdateSrtStream } from '../../../connection/video';
import MediaVideo from '../../general-components/media-video';
import DefaultSafetyImage from '../../../images/default-safety-image.png';

const TARGET_TIME = 1; // ideal current time of the srt stream
const TARGET_TIME_DIFF_THRESHOLD = 2; // the max difference from the target time
const TARGET_TIME_DIFF_APPLIED = TARGET_TIME + TARGET_TIME_DIFF_THRESHOLD;

const doEchoCancel = shouldDoEchoCancel();

interface ActiveSrtStreamProps {
	id: number;
	playbackId: string;
	thumbnail: string;
	feedPosition: number;
	localMuted: boolean;
	adjustedVolume: number;
	embed: boolean;
}

export function ActiveSrtStream(props: ActiveSrtStreamProps) {
	const {
		id,
		playbackId,
		thumbnail,
		feedPosition,
		localMuted,
		adjustedVolume,
		embed,
	} = props;

	const video = useRef<HTMLVideoElement | null>(null);
	const audio = useRef<HTMLAudioElement | null>(null);

	useEchoCancellation(video, audio);

	useEffect(() => {
		if (doEchoCancel && audio.current) {
			audio.current.volume = localMuted ? 0 : adjustedVolume;
		}
		if (!doEchoCancel && video.current) {
			video.current.volume = localMuted ? 0 : adjustedVolume;
		}
	}, [
		video.current,
		audio.current,
		localMuted,
		adjustedVolume,
	]);

	return (
		<>
			<MediaVideo
				id={`srt-stream-${id}-${feedPosition}`}
				className="media-video"
				src={`https://stream.mux.com/${playbackId}.m3u8${embed ? '?min_resolution=1080p' : ''}`}
				isHlsVideo={true}
				crossOrigin={undefined}
				ref={video}
				loop={false}
				muted={false}
				onLoadedData={({ currentTarget }) => {
					if (currentTarget) {
						const desiredVolume = localMuted ? 0 : adjustedVolume;
						if (currentTarget.volume !== 0) {
							currentTarget.volume = 0;
						}
						if (doEchoCancel && audio.current && audio.current.volume !== desiredVolume) {
							audio.current.volume = desiredVolume;
						}
						if (!doEchoCancel && video.current && video.current.volume !== desiredVolume) {
							video.current.volume = desiredVolume;
						}
						currentTarget.play();
					}
				}}
				onPause={({ currentTarget }) => {
					// an active srt stream should always be playing
					if (currentTarget) {
						currentTarget.play();
					}
				}}
				onTimeUpdate={({ currentTarget }) => {
					// if the difference exceeds the target time with the threshold applied, set to ideal time
					if (
						currentTarget
						&& currentTarget.currentTime < currentTarget.duration - TARGET_TIME_DIFF_APPLIED
					) {
						console.log('syncing srt stream');
						currentTarget.currentTime = currentTarget.duration - TARGET_TIME;
					}
				}}
				duration={video.current?.duration ?? 0}
				elapsed={video.current?.currentTime ?? 0}
				posterImage={thumbnail}
			/>
			{doEchoCancel && <audio ref={audio}/>}
		</>
	);
}

interface FeedSrtStreamProps {
	feedPosition: number;
	srtStream: SrtStream | undefined;
	audioOnly: boolean;
	isSpotlightFeed?: boolean;
	dropCallback: DropCallback;
}

export default function FeedSrtStream(props: FeedSrtStreamProps) {
	const {
		feedPosition,
		srtStream,
		audioOnly,
		isSpotlightFeed = false,
		dropCallback,
	} = props;

	if (!srtStream) return <></>;

	const {
		id,
		playbackId,
		status,
		thumbnail,
		safetyImage,
	} = srtStream;

	const globalIsDragging = useSelector((state: GS) => state.UserInterfaceReducer.globalIsDragging);
	const backstageVolume = useSelector((state: GS) => state.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((state: GS) => state.MeetingReducer.roomState);
	const embed = useSelector((state: GS) => state.SessionReducer.embed);
	const token = useSelector((state: GS) => state.SessionReducer.token);
	const meeting = useSelector((state: GS) => state.SessionReducer.meeting);

	const [isHovering, setIsHovering] = useState<boolean>(false);

	const { isOver, dropRef } = useDropArea({
		accepts: DRAG_TYPE.FEEDS,
		dropCallback,
		dropAreaData: { feedPosition },
	});

	const {
		currentShot: {
			shot_data: {
				forcedLayout,
				shotFeeds,
			},
		},
		isOnCanvas,
	} = useShots();

	const {
		localVolume,
		localMuted,
		handleMuteChange,
		handleVolumeChange,
	} = useAudioControls(srtStream, ({ volume, muted }) => {
		if (!token || !meeting) return;
		UpdateSrtStream(token, meeting.meeting, id, { volume, muted });
	});

	const isShotOnCanvas = useMemo(
		() => shotFeeds.some((f) => f.feedType === FEED_TYPES.SRT_STREAM && f.srtStreamId === id),
		[shotFeeds],
	);

	const isOffscreenSpotlight = useMemo(
		() => !isShotOnCanvas && isSpotlightFeed,
		[isShotOnCanvas, isSpotlightFeed],
	);

	const adjustedVolume = useMemo(() => {
		if (isOffscreenSpotlight) return 0;
		if (isOnCanvas || embed || roomState === ROOM_STATES.EDITING) return localVolume;
		return localVolume * backstageVolume;
	}, [backstageVolume, isOnCanvas, embed, roomState, localVolume, isOffscreenSpotlight]);

	const isHiddenTwoUpShot = useMemo(
		(): boolean => feedPosition === 0
			&& forcedLayout === V_LAYOUTS.TWO.FULL_TWO,
		[forcedLayout, feedPosition],
	);

	const volumeScrubCss = useMemo(
		() => {
			const percent = localMuted ? 0 : localVolume * 100;
			const gray = 'rgba(255, 255, 255, 0.3)';
			return {
				background: `linear-gradient(to right, white 0%, white ${percent}%, ${gray} ${percent}%, ${gray} 100%)`,
			};
		},
		[localMuted, localVolume],
	);

	return (
		<>
			{isOver && <div className="dnd-highlighted" />}
			<div
				className="media"
				ref={dropRef}
				onMouseEnter={() => setIsHovering(true)}
				onMouseLeave={() => setIsHovering(false)}
			>
				<div className="flex-center height-full width-full">
					{status === 'active' ? (
						<ActiveSrtStream
							id={id}
							playbackId={playbackId}
							thumbnail={thumbnail}
							feedPosition={feedPosition}
							localMuted={localMuted}
							adjustedVolume={adjustedVolume}
							embed={embed}
						/>
					) : (
						<img
							className="media-img"
							src={safetyImage}
							alt="Safety Image"
							onError={({ currentTarget }) => {
								currentTarget.src = DefaultSafetyImage;
							}}
						/>
					)}
				</div>
			</div>
			{srtStream && !embed && !globalIsDragging && !isOffscreenSpotlight && (
				<Portal
					targetId={isSpotlightFeed ? 'controls-container-live' : 'controls-container-editing'}
					className={classNames(
						`media-controls-layer width-full height-full ${videoGridClasses[feedPosition]}`,
						{
							hover: isHovering,
							'hidden-feed': isHiddenTwoUpShot,
							'display-none': audioOnly,
						},
					)}
				>
					<div
						className="flex-justify-flex-end width-full"
						onMouseEnter={() => setIsHovering(true)}
						onMouseLeave={() => setIsHovering(false)}
					>
						<div className="align-items-center media-controls" data-testid='videoMediaControlsVisible'>
							<div
								className={classNames('flex-center margin-left-medium', { localMuted })}
								onClick={handleMuteChange}
							>
								<Icon
									name={localMuted ? ICONS.AUDIO_MUTED : ICONS.SOUND}
									size={18}
									color={localMuted ? COLORS.GRAY : COLORS.WHITE}
								/>
							</div>
							<div className="align-items-center position-relative padding-left-medium vol-wrap">
									<input
										type="range"
										className="volume"
										onChange={handleVolumeChange}
										value={localMuted ? 0 : localVolume}
										step={0.01}
										min={0}
										max={1}
										style={volumeScrubCss}
									/>
							</div>
						</div>
					</div>
				</Portal>
			)}
		</>
	);
}
